<script lang="ts" setup>

    import { ref, watch } from "vue";
    import SearchBase  from './SearchBase.vue';
    import { MenuNameType } from "../../Enums/ChoiceEnums";
    import { documentManager } from '../../lib/ChoiceApi';
    import { ISearchDocumentFolderItem, ISearchDocumentFolderInputs, ISearchDocumentFolderKey, ISearchBaseInputs } from "../../lib/Models/SearchModel";

    const emit = defineEmits<{
        (e: "onChange", selectedItem: ISearchDocumentFolderItem): void
    }>();

    const props = withDefaults(defineProps<{
        // Placeholder text to be displayed inside text control if no values
        placeholder?: string,
        // Label for the component
        label?: string,
        // The default search value 
        defaultSearchKey?: ISearchDocumentFolderKey,
        // The maximum height of the search results
        maxHeight?: number,
        // The folder location
        menuNameId: MenuNameType,
        // Reset Focus
        resetFocus?: boolean,
        // Perform auto-search on text input
        enableAutoSearch?: boolean,
        // Show the clear search function
        showClearSearch?: boolean
    }>(), {
        placeholder: "Search Folder",
        label: "Folder"
    });

    const searchColumns = ref(
        [
            { title: "#", element: "folder_menu_id" },
            { title: "Cabinet", element: "cabinet_name" },
            { title: "Drawer", element: "drawer_name" },
            { title: "Folder", element: "folder_name" },
        ],        
    );
    const searchResults = ref<ISearchDocumentFolderItem[]>([]);    
    const defaultSearchText = ref<string>("");
    const processing = ref<boolean>(false);

    watch(() => props.defaultSearchKey, (newValue, oldValue) => {
        defaultSearchText.value = "";
        if (newValue?.display) {
            defaultSearchText.value = newValue.display;
            const selectedItem: ISearchDocumentFolderItem = {
                folder_menu_id: newValue.menuId,
                path: newValue.display
            };
            onChange(selectedItem);
        }
        else if (newValue?.menuId) {
            const searchInputs: ISearchDocumentFolderInputs = { 
                menuNameId: props.menuNameId,
                menuId: newValue.menuId, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else if (newValue?.searchText) {
            defaultSearchText.value = newValue.searchText;
            const selectedItem: ISearchDocumentFolderItem = {};
            onChange(selectedItem);
            const searchInputs: ISearchDocumentFolderInputs = { 
                menuNameId: props.menuNameId,
                searchText: newValue.searchText, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else  {
            defaultSearchText.value = "";
            const selectedItem: ISearchDocumentFolderItem = {};
            onChange(selectedItem);
        }
    });

    async function performSearch(inputs: ISearchDocumentFolderInputs) {
        const respSearch = await documentManager.searchFolder(inputs);
        searchResults.value = respSearch.searchList || [];
    }

    async function performSearchBase(inputs: ISearchBaseInputs)  {
        const searchInputs: ISearchDocumentFolderInputs = { 
            menuNameId: props.menuNameId, 
            searchText: inputs.searchText,
            displayAllResults: false 
        };
        performSearch(searchInputs);
    }

    function onChange(selectedItem: ISearchDocumentFolderItem) {
        emit("onChange", selectedItem);
        defaultSearchText.value = selectedItem?.path ?? "";
    }

</script>

<template>
    <search-base 
        :placeholder="placeholder" 
        :label="label" 
        :columns="searchColumns" 
        displayColumn="path"
        :searchResults="searchResults"
        :defaultSearchText="defaultSearchText"
        :processing="processing"
        :enableAutoSearch="enableAutoSearch"
        :showClearSearch="showClearSearch"
        :resetFocus="resetFocus"
        :maxHeight="maxHeight"
        @performSearchBase="performSearchBase"
        @onChange="onChange">
    </search-base>
</template>